import React, { useState, useEffect, useRef } from 'react';
import { useBooks } from '../context/BookContext';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import BookCard from '../components/BookCard';
import AddBookModal from '../components/AddBookModal';
import BookDetailModal from '../components/BookDetailModal';
import TbrTableStack from '../components/TbrTableStack';
import Bookshelf from '../components/Bookshelf';
import TreeShelf from '../components/TreeShelf';
import MyShelves from '../components/MyShelves';
import MyCurations from '../components/MyCurations';
import PostcardShare from '../components/PostcardShare';
import { Plus, Search, X, MapPin, Pencil, LayoutGrid, TreeDeciduous, Image as ImageIcon, Home, Sparkles, List } from 'lucide-react';

const SHELF_LABELS = { reading: 'Reading', tbr: 'TBR', read: 'Read', owned: 'Collection' };
const VIEW_MODE_STORAGE_KEY = 'll_library_view_mode';

const Library = () => {
  const { books, loading, deleteBook, updateBook } = useBooks();
  const { user, profile } = useAuth();

  // The visual shelf is the primary/default view of the library. 'grid' is
  // the original status-tab card list, still reachable via the toggle.
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    return ['shelf', 'tree', 'myshelves', 'curations', 'grid'].includes(saved) ? saved : 'shelf';
  });
  const setAndPersistViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
  };

  const [filter, setFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [shelfLocations, setShelfLocations] = useState({});
  const [isEditingLocation, setIsEditingLocation] = useState(false);
  const [locationInput, setLocationInput] = useState('');
  const [showPostcard, setShowPostcard] = useState(false);
  const shelfSnapshotRef = useRef(null);
  const treeSnapshotRef = useRef(null);

  const readBooks = books.filter(book => book.status === 'read');
  const activeSnapshotRef = viewMode === 'tree' ? treeSnapshotRef : shelfSnapshotRef;

  // Debounce the search input so filtering doesn't run on every keystroke
  useEffect(() => {
    const handle = setTimeout(() => {
      setSearchTerm(searchInput.trim().toLowerCase());
    }, 250);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Load this user's shelf location labels (one row per shelf_key)
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    supabase
      .from('shelf_locations')
      .select('shelf_key, location_label')
      .eq('user_id', user.id)
      .then(({ data, error }) => {
        if (cancelled || error || !data) return;
        const map = {};
        data.forEach(row => { map[row.shelf_key] = row.location_label; });
        setShelfLocations(map);
      });
    return () => { cancelled = true; };
  }, [user]);

  const statusFilteredBooks = filter === 'all'
    ? books
    : books.filter(b => b.status === filter);

  const filteredBooks = searchTerm
    ? statusFilteredBooks.filter(b => {
        const title = (b.title || '').toLowerCase();
        const author = (b.author || '').toLowerCase();
        const isbn = (b.isbn || '').toLowerCase();
        return title.includes(searchTerm) || author.includes(searchTerm) || isbn.includes(searchTerm);
      })
    : statusFilteredBooks;

  const startEditingLocation = () => {
    setLocationInput(shelfLocations[filter] || '');
    setIsEditingLocation(true);
  };

  const saveLocation = async () => {
    const label = locationInput.trim();
    setIsEditingLocation(false);
    if (!user || filter === 'all') return;
    setShelfLocations(prev => ({ ...prev, [filter]: label || null }));
    await supabase
      .from('shelf_locations')
      .upsert({ user_id: user.id, shelf_key: filter, location_label: label || null, updated_at: new Date().toISOString() }, { onConflict: 'user_id,shelf_key' });
  };

  if (loading) return <div style={{ textAlign: 'center', marginTop: '3rem', color: '#999' }}>Loading Sanctuary...</div>;

  return (
    <div>
      <div style={headerStyle}>
        <div>
          <h2 style={{ margin: 0 }}>My Library</h2>
        </div>

        <div style={headerActionsStyle}>
          {(viewMode === 'shelf' || viewMode === 'tree') && readBooks.length > 0 && (
            <button
              style={postcardButtonStyle}
              onClick={() => setShowPostcard(true)}
              title="Share a postcard of your shelf"
            >
              <ImageIcon size={18} />
            </button>
          )}
          <div style={toggleContainerStyle}>
            <button style={viewMode === 'shelf' ? activeToggleStyle : toggleStyle} onClick={() => setAndPersistViewMode('shelf')} title="Standard Shelf">
              <LayoutGrid size={18} />
            </button>
            <button style={viewMode === 'tree' ? activeToggleStyle : toggleStyle} onClick={() => setAndPersistViewMode('tree')} title="Tree of Knowledge">
              <TreeDeciduous size={18} />
            </button>
            <button style={viewMode === 'myshelves' ? activeToggleStyle : toggleStyle} onClick={() => setAndPersistViewMode('myshelves')} title="My Shelves (real, named shelves by location)">
              <Home size={18} />
            </button>
            <button style={viewMode === 'curations' ? activeToggleStyle : toggleStyle} onClick={() => setAndPersistViewMode('curations')} title="My Curations (personal book selections)">
              <Sparkles size={18} />
            </button>
            <button style={viewMode === 'grid' ? activeToggleStyle : toggleStyle} onClick={() => setAndPersistViewMode('grid')} title="List View">
              <List size={18} />
            </button>
          </div>
          {viewMode === 'grid' && (
            <button style={addButtonStyle} onClick={() => setIsModalOpen(true)} title="Add New Book">
              <Plus size={20} />
            </button>
          )}
        </div>
      </div>

      {viewMode !== 'grid' && (
        <button
          style={fabAddButtonStyle}
          onClick={() => setIsModalOpen(true)}
          title="Add New Book"
          aria-label="Add New Book"
        >
          <Plus size={24} />
        </button>
      )}

      {viewMode === 'myshelves' ? (
        <MyShelves onBookClick={setSelectedBook} />
      ) : viewMode === 'curations' ? (
        <MyCurations />
      ) : viewMode === 'shelf' || viewMode === 'tree' ? (
        readBooks.length === 0 ? (
          <div style={emptyShelfStyle}>
            <p>Your shelf is empty! Finish a book to see it here.</p>
          </div>
        ) : viewMode === 'shelf' ? (
          <Bookshelf ref={shelfSnapshotRef} books={readBooks} onBookClick={setSelectedBook} />
        ) : (
          <TreeShelf ref={treeSnapshotRef} books={readBooks} onBookClick={setSelectedBook} />
        )
      ) : (
        <>
          <div style={tabContainerStyle}>
            {['all', 'reading', 'tbr', 'read', 'owned'].map(t => (
              <button
                key={t}
                onClick={() => setFilter(t)}
                style={{
                  ...tabStyle,
                  color: filter === t ? 'var(--accent)' : 'var(--text-soft)',
                  borderBottom: filter === t ? '2px solid var(--accent)' : '2px solid transparent'
                }}
              >
                {t === 'owned' ? 'Collection' : t === 'tbr' ? 'TBR' : t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>

          {filter !== 'all' && (
            isEditingLocation ? (
              <div style={locationEditRowStyle}>
                <MapPin size={14} color="var(--text-soft)" style={{ flexShrink: 0 }} />
                <input
                  autoFocus
                  type="text"
                  value={locationInput}
                  onChange={(e) => setLocationInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveLocation(); if (e.key === 'Escape') setIsEditingLocation(false); }}
                  placeholder={`Where does your ${SHELF_LABELS[filter] || filter} shelf live? e.g. Living Room Bookcase`}
                  style={locationInputStyle}
                />
                <button onClick={saveLocation} style={locationSaveButtonStyle}>Save</button>
              </div>
            ) : (
              <div style={locationTagRowStyle} onClick={startEditingLocation} title="Edit shelf location">
                <MapPin size={13} color="var(--text-soft)" />
                <span style={locationTagTextStyle}>
                  {shelfLocations[filter] ? `Lives on: ${shelfLocations[filter]}` : 'Add a physical location'}
                </span>
                <Pencil size={11} color="var(--text-soft)" />
              </div>
            )
          )}

          <div style={searchBoxStyle}>
            <Search size={16} color="var(--text-soft)" style={{ flexShrink: 0 }} />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by title, author, or ISBN..."
              style={searchInputStyle}
            />
            {searchInput && (
              <button
                onClick={() => setSearchInput('')}
                style={clearSearchButtonStyle}
                title="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {filteredBooks.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#999', marginTop: '3rem' }}>
              {searchTerm ? 'No books match your search.' : 'No books found in this category.'}
            </p>
          ) : filter === 'tbr' ? (
            <TbrTableStack books={filteredBooks} onBookSelect={setSelectedBook} />
          ) : (
            <div style={gridStyle}>
              {filteredBooks.map(book => (
                <div key={book.id} onClick={() => setSelectedBook(book)} style={{ cursor: 'pointer' }}>
                  <BookCard
                    book={book}
                    onDelete={deleteBook}
                    onUpdate={updateBook}
                  />
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {isModalOpen && <AddBookModal onClose={() => setIsModalOpen(false)} />}

      {selectedBook && (
        <BookDetailModal
          book={selectedBook}
          onClose={() => setSelectedBook(null)}
        />
      )}

      {showPostcard && (
        <PostcardShare
          shelfRef={activeSnapshotRef}
          viewMode={viewMode}
          displayName={profile?.username}
          onClose={() => setShowPostcard(false)}
        />
      )}
    </div>
  );
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '1rem',
  flexWrap: 'wrap',
  gap: '10px'
};

const headerActionsStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px'
};

const postcardButtonStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '8px',
  border: '1px solid var(--border)',
  background: 'var(--bg-card)',
  borderRadius: '8px',
  cursor: 'pointer',
  color: 'var(--text-main)'
};

const toggleContainerStyle = {
  display: 'flex',
  backgroundColor: 'var(--bg-app)',
  borderRadius: '8px',
  padding: '4px',
  border: '1px solid var(--border)'
};

const toggleStyle = {
  padding: '6px',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  color: 'var(--text-soft)',
  borderRadius: '6px',
  display: 'flex',
  alignItems: 'center'
};

const activeToggleStyle = {
  ...toggleStyle,
  backgroundColor: 'var(--bg-card)',
  color: 'var(--accent)',
  boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
};

const addButtonStyle = {
  backgroundColor: 'var(--accent)',
  color: 'white',
  border: 'none',
  width: '36px',
  height: '36px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  flexShrink: 0
};

const fabAddButtonStyle = {
  position: 'fixed',
  right: '24px',
  bottom: '24px',
  backgroundColor: 'var(--accent)',
  color: 'white',
  border: 'none',
  width: '52px',
  height: '52px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
  zIndex: 500
};

const emptyShelfStyle = {
  padding: '40px',
  backgroundColor: '#f5f5f5',
  borderRadius: '8px',
  textAlign: 'center',
  border: '2px dashed #ccc'
};

const tabContainerStyle = {
  display: 'flex',
  gap: '1rem',
  marginBottom: '1.5rem',
  borderBottom: '1px solid var(--border)'
};

const searchBoxStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 12px',
  marginBottom: '1.5rem',
  backgroundColor: 'var(--bg-card)',
  border: '1px solid var(--border)',
  borderRadius: '10px'
};

const searchInputStyle = {
  flex: 1,
  border: 'none',
  outline: 'none',
  background: 'none',
  fontSize: '0.9rem',
  color: 'var(--text-main)'
};

const clearSearchButtonStyle = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: 'var(--text-soft)',
  display: 'flex',
  alignItems: 'center',
  padding: 0
};

const tabStyle = {
  background: 'none',
  border: 'none',
  padding: '8px 4px',
  cursor: 'pointer',
  fontSize: '0.9rem',
  fontWeight: '500',
  transition: 'all 0.2s',
  color: 'var(--text-soft)'
};

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
  gap: '1rem'
};

const locationTagRowStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '4px 10px',
  marginBottom: '1rem',
  backgroundColor: 'var(--bg-card)',
  border: '1px solid var(--border)',
  borderRadius: '20px',
  cursor: 'pointer',
  fontSize: '0.78rem',
  color: 'var(--text-soft)'
};

const locationTagTextStyle = {
  whiteSpace: 'nowrap'
};

const locationEditRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '1rem'
};

const locationInputStyle = {
  flex: 1,
  padding: '6px 10px',
  fontSize: '0.85rem',
  border: '1px solid var(--border)',
  borderRadius: '8px',
  background: 'var(--bg-card)',
  color: 'var(--text-main)',
  outline: 'none'
};

const locationSaveButtonStyle = {
  padding: '6px 12px',
  fontSize: '0.8rem',
  border: 'none',
  borderRadius: '8px',
  backgroundColor: 'var(--accent)',
  color: 'white',
  cursor: 'pointer'
};

export default Library;
